import { mapGetters } from "vuex";
import { isEmpty } from "@utils";
import { PAGES } from "@consts";

export default {
	computed: {
		...mapGetters("navigation", [
			"isWordListResultsPage",
			"isWordListCombinationResultsPage",
			"isWordListCombinationLengthResultsPage",
			"isWordListSpecialResultsPage",
			"isAnyWordListResultsPage",
			"isWordsByLengthResultsPage",
			"isWordsWithResultsPage",
			"isWordsStartingResultsPage",
			"isWordsEndingResultsPage",
		]),
		/**
		 * @todo refactor the breadcrumb's component to use this computed property instead, so it's centrally managed.
		 */
		wordListBreadcrumbParamDisplay() {
			return "";
		},
	},
	methods: {
		/**
		 * Generates the H1 text for the current page.
		 * @param {string|number} wordLength optional parameter to specify if this is for a specific word length table
		 * @returns {string} the formatted string
		 */
		getWordListH1Text(wordLength = null) {
			const advancedSearchOptions = this.searchParams?.advanced || {};

			if (this.isWordListCombinationResultsPage) {
				const startsParam = (
					advancedSearchOptions.starts || ""
				).toUpperCase();
				const withParam = (
					advancedSearchOptions.contains || ""
				).toUpperCase();
				const endsParam = (
					advancedSearchOptions.ends || ""
				).toUpperCase();
				const lengthParam = advancedSearchOptions.length || null;

				const lengthWordsPart = !isEmpty(lengthParam)
					? `${lengthParam} Letter Words`
					: "Words";
				const startWordsPart = !isEmpty(startsParam)
					? `Starting with ${startsParam}`
					: "";
				const withWordsPart = !isEmpty(withParam)
					? `Containing ${withParam}`
					: "";
				const endWordsPart = !isEmpty(endsParam)
					? `Ending in ${endsParam}`
					: "";

				const letterParams = [
					startWordsPart,
					withWordsPart,
					endWordsPart,
				].filter((variable) => !isEmpty(variable));

				const letterParamsLength = letterParams.length;
				if (letterParamsLength > 1) {
					letterParams.splice(-1, 0, " and ");
				}

				if (letterParamsLength > 2) {
					letterParams.splice(1, 0, ", ");
				}

				return `${lengthWordsPart} ${letterParams.join("")}`;
			}

			const wordLengthPrefix =
				wordLength > 0 ? wordLength.toString() : "";
			if (this.isWordsWithResultsPage) {
				return `${wordLengthPrefix} Letter Words With ${advancedSearchOptions.contains?.toUpperCase()}`;
			}
			if (this.isWordsStartingResultsPage) {
				return `${wordLengthPrefix} Letter Words That Start With ${advancedSearchOptions.starts?.toUpperCase()}`;
			}
			if (this.isWordsEndingResultsPage) {
				return `${wordLengthPrefix} Letter Words That End in ${advancedSearchOptions.ends?.toUpperCase()}`;
			}

			/**
			 * @note the standard simple Letter Words page only
			 * has one table, so it functions just like the
			 * All Words grouping mode in any other result page
			 */
			if (this.isWordsByLengthResultsPage) {
				return this.$t("UnscramblePageTotalPointsHeaderContainer");
			}

			return this.$t("ResultsWordsNumberTableTitle", {
				numberQuery: wordLength,
			});
		},
		/**
		 * Generates the table heading text for the result tables.
		 * @param {string|number} wordLength mandatory parameter specifying the length of the table words
		 * @returns {string} the table heading  string
		 */
		getWordListTableHeadingText(wordLength = 0) {
			if (
				this.isWordsByLengthResultsPage ||
				this.isWordListCombinationLengthResultsPage
			) {
				return `${wordLength} Letter Word List`;
			}

			return this.$t("ResultsWordsNumberTableTitle", {
				numberQuery: wordLength,
			});
		},
		getH1Text() {
			return (
				this.getBodyParams()?.h1 ||
				this.getWordListH1Text(this.searchParams?.advanced?.length) // Build the h1 text if it isn't explicitly defined in the body params
			);
		},
		getSlug() {
			return this.getBodyParams()?.slug;
		},
		getBelowResultsAnchor() {
			return this.getBodyParams()?.belowResultsAnchor;
		},
		getNonCMSWordListMetaInfo() {
			/**
			 * @note to avoid the WFW-5565 PR from becoming waaaaay too big,
			 * I'm not refactoring this to use the new `getSearchParamsFromRoute`
			 * method just yet.
			 * Marking it as a @todo for now.
			 */
			let metaTitle = "";
			let metaDescription = "";

			if (
				[
					PAGES.WORDS_HAVING_RESULTS,
					PAGES.WORDS_STARTING_RESULTS,
					PAGES.WORDS_ENDING_RESULTS,
				].includes(this.$route.name)
			) {
				const userQuery = this.$route.params?.letter?.toUpperCase();

				switch (this.$route.name) {
					case PAGES.WORDS_HAVING_RESULTS:
						metaTitle = this.$t("WordsWithPageMetaTitle", {
							ContainQuery: userQuery,
						});
						metaDescription = this.$t(
							"WordsWithPageMetaDescription",
							{
								ContainQuery: userQuery,
							}
						);
						break;
					case PAGES.WORDS_STARTING_RESULTS:
						metaTitle = this.$t("WordsStartingPageMetaTitle", {
							StartQuery: userQuery,
						});
						metaDescription = this.$t(
							"WordsStartingPageMetaDescription",
							{
								StartQuery: userQuery,
							}
						);
						break;
					case PAGES.WORDS_ENDING_RESULTS:
						metaTitle = this.$t("WordsEndingPageMetaTitle", {
							EndQuery: userQuery,
						});
						metaDescription = this.$t(
							"WordsEndingPageMetaDescription",
							{
								EndQuery: userQuery,
							}
						);
						break;
				}

				return { metaTitle, metaDescription };
			}

			if (this.$route.name === PAGES.WORDS_BY_LENGTH_RESULTS) {
				const length = this.$route.params.length;

				return {
					metaTitle: this.$t("WordsByNumberPageMetaTitle", {
						numberQuery: length,
					}),
					metaDescription: this.$t(
						"WordsByNumberPageMetaDescription",
						{
							numberQuery: length,
						}
					),
				};
			}

			if (
				[
					PAGES.WORDS_COMBINATION_LETTERS_RESULTS,
					PAGES.WORDS_COMBINATION_LENGTH_AND_LETTERS_RESULTS,
				].includes(this.$route.name)
			) {
				const advancedSearchOptions = this.searchParams?.advanced || {};

				// Build the key.
				const i18nKeyStub = ["Words"];
				if (advancedSearchOptions.starts) {
					i18nKeyStub.push("Starts");
				}
				if (advancedSearchOptions.contains) {
					i18nKeyStub.push("Contains");
				}
				if (advancedSearchOptions.ends) {
					i18nKeyStub.push("Ends");
				}
				if (advancedSearchOptions.length) {
					i18nKeyStub.push("Length");
				}

				i18nKeyStub.push("DetailPage");

				const i18nMetaTitleKey = i18nKeyStub
					.concat(["MetaTitle"])
					.join("");
				const i18nMetaDescriptionKey = i18nKeyStub
					.concat(["MetaDescription"])
					.concat(
						advancedSearchOptions.length?.toString() === "5"
							? ["WithWordle"]
							: []
					)
					.join("");

				const {
					starts: StartQuery,
					contains: ContainQuery,
					ends: EndQuery,
					length: numberQuery,
				} = advancedSearchOptions;

				const i18nAlias = {
					StartQuery: StartQuery?.toUpperCase(),
					ContainQuery: ContainQuery?.toUpperCase(),
					EndQuery: EndQuery?.toUpperCase(),
					numberQuery,
				};

				return {
					metaTitle: this.$t(i18nMetaTitleKey, i18nAlias),
					metaDescription: this.$t(i18nMetaDescriptionKey, i18nAlias),
				};
			}
		},
		getNonCMSCopySections() {
			const advancedSearchOptions = this.searchParams?.advanced || {};

			if (
				[
					PAGES.WORDS_HAVING_RESULTS,
					PAGES.WORDS_STARTING_RESULTS,
					PAGES.WORDS_ENDING_RESULTS,
				].includes(this.$route.name)
			) {
				let prefix = "";
				let firstSuggestion = "";
				let secondSuggestion = "";

				let letter;

				switch (this.$route.name) {
					case PAGES.WORDS_HAVING_RESULTS:
						prefix = "Words with the Letters";
						letter =
							advancedSearchOptions.contains?.toUpperCase() || "";
						firstSuggestion = this.getWordListSuggestionLink(
							"starts",
							letter
						);
						secondSuggestion = this.getWordListSuggestionLink(
							"ends",
							letter
						);
						break;
					case PAGES.WORDS_STARTING_RESULTS:
						prefix = "Words that Start with ";
						letter =
							advancedSearchOptions.starts?.toUpperCase() || "";
						firstSuggestion = this.getWordListSuggestionLink(
							"ends",
							letter
						);
						secondSuggestion = this.getWordListSuggestionLink(
							"contains",
							letter
						);
						break;
					case PAGES.WORDS_ENDING_RESULTS:
						prefix = "Words Ending in";
						letter =
							advancedSearchOptions.ends?.toUpperCase() || "";
						firstSuggestion = this.getWordListSuggestionLink(
							"starts",
							letter
						);
						secondSuggestion = this.getWordListSuggestionLink(
							"contains",
							letter
						);
						break;
				}

				return [
					this.$t("SuggestedLinksSimpleWordLists", {
						Prefix: prefix,
						UserQuery: letter,
						FirstLink: firstSuggestion,
						SecondLink: secondSuggestion,
					}),
				];
			}

			if (
				[
					PAGES.WORDS_COMBINATION_LETTERS_RESULTS,
					PAGES.WORDS_COMBINATION_LENGTH_AND_LETTERS_RESULTS,
				].includes(this.$route.name)
			) {
				// Build the key.
				const i18nKeyStub = ["Words"];
				if (advancedSearchOptions.starts) {
					i18nKeyStub.push("Starts");
				}
				if (advancedSearchOptions.contains) {
					i18nKeyStub.push("Contains");
				}
				if (advancedSearchOptions.ends) {
					i18nKeyStub.push("Ends");
				}
				if (advancedSearchOptions.length) {
					i18nKeyStub.push("Length");
				}

				i18nKeyStub.push("DetailPageText");
				let i18nKeyToUse = i18nKeyStub.join("");

				i18nKeyToUse =
					advancedSearchOptions.length?.toString?.() === "5"
						? `${i18nKeyToUse}WithWordle`
						: i18nKeyToUse;
				const i18nParams = {};

				/**
				 * @note
				 * The way the links are generated and shown can be a little tricky,
				 * so here is a quick guide.
				 *
				 * If there is only 1 param, then the links are built from the other 2
				 * text-base fields (e.g. if the query is only Starts, then the links are
				 * Contains and Ends for the same letters). You don't have to worry about
				 * the length param, because it will either have a CMS-based copy, or (beyond
				 * a certain length value) will just not have copy at all.
				 *
				 * For multiple params, GG.
				 */

				const params = { ...advancedSearchOptions };
				const paramsWithoutLength = Object.entries(
					advancedSearchOptions
				)
					.filter(([key]) =>
						["starts", "contains", "ends"].includes(key)
					)
					.reduce((acc, [key, value]) => {
						acc[key] = value;
						return acc;
					}, {});

				i18nParams.StartsQuery =
					advancedSearchOptions.starts?.toUpperCase() ||
					this.suggestedLinks.starts?.toUpperCase();
				i18nParams.ContainQuery =
					advancedSearchOptions.contains?.toUpperCase() ||
					this.suggestedLinks.contains?.toUpperCase();
				i18nParams.EndsQuery =
					advancedSearchOptions.ends?.toUpperCase() ||
					this.suggestedLinks.ends?.toUpperCase();
				i18nParams.LengthQuery =
					advancedSearchOptions.length || this.suggestedLinks.len;

				if (Object.keys(paramsWithoutLength).length === 1) {
					params.letter = Object.values(paramsWithoutLength)[0];

					i18nParams.StartsLink = this.getWordListSuggestionLink(
						"starts",
						params.letter
					);
					i18nParams.ContainLink = this.getWordListSuggestionLink(
						"contains",
						params.letter
					);
					i18nParams.EndsLink = this.getWordListSuggestionLink(
						"ends",
						params.letter
					);
					i18nParams.LengthLink = this.getWordListSuggestionLink(
						"len",
						advancedSearchOptions.length
					);
					i18nParams.LengthStartsLink =
						this.getWordListSuggestionLink(
							"starts_len",
							advancedSearchOptions.length,
							params.letter
						);
					i18nParams.LengthContainLink =
						this.getWordListSuggestionLink(
							"contains_len",
							advancedSearchOptions.length,
							params.letter
						);
					i18nParams.LengthEndsLink = this.getWordListSuggestionLink(
						"ends_len",
						advancedSearchOptions.length,
						params.letter
					);
				} else {
					i18nParams.StartsLink = this.getWordListSuggestionLink(
						"starts",
						advancedSearchOptions.starts
					);
					i18nParams.ContainLink = this.getWordListSuggestionLink(
						"contains",
						advancedSearchOptions.contains
					);
					i18nParams.EndsLink = this.getWordListSuggestionLink(
						"ends",
						advancedSearchOptions.ends
					);
					i18nParams.LengthLink = this.getWordListSuggestionLink(
						"len",
						advancedSearchOptions.length
					);
					i18nParams.LengthStartsLink =
						this.getWordListSuggestionLink(
							"starts_len",
							advancedSearchOptions.length,
							advancedSearchOptions.starts
						);
					i18nParams.LengthEndsLink = this.getWordListSuggestionLink(
						"ends_len",
						advancedSearchOptions.length,
						advancedSearchOptions.ends
					);
				}

				return [this.$t(i18nKeyToUse, i18nParams)];
			}
		},
		getWordListSuggestionLink(type, firstParam, secondParam) {
			const advancedSearchOptions = this.searchParams?.advanced || {};
			const isMultipleParams =
				Object.values(advancedSearchOptions).filter(
					(value) => !isEmpty(value)
				).length > 1;

			switch (type) {
				case "starts":
					return this.formatWordListSuggestedLink(
						`words that start with ${firstParam?.toUpperCase()}`,
						!isEmpty(this.suggestedLinks.starts)
							? `/words-that-start/${firstParam?.toLowerCase()}/`
							: null
					);
				case "contains": {
					const textString = isMultipleParams
						? `words with ${firstParam?.toUpperCase()}`
						: `words that contain ${firstParam?.toUpperCase()}`;

					return this.formatWordListSuggestedLink(
						textString,
						!isEmpty(this.suggestedLinks.contains)
							? `/words-with-the-letter/${firstParam?.toLowerCase()}/`
							: null
					);
				}
				case "ends": {
					const textString = isMultipleParams
						? `words that end in ${firstParam?.toUpperCase()}`
						: `words that end with ${firstParam?.toUpperCase()}`;
					return this.formatWordListSuggestedLink(
						textString,
						!isEmpty(this.suggestedLinks.ends)
							? `/ending-with/${firstParam?.toLowerCase()}/`
							: null
					);
				}
				case "len":
					return this.formatWordListSuggestedLink(
						`${firstParam} letter words`,
						!isEmpty(this.suggestedLinks.len)
							? `/letter-words/${firstParam}/`
							: null
					);
				case "starts_len":
					return this.formatWordListSuggestedLink(
						`${firstParam} letter words that start with ${secondParam?.toUpperCase()}`,
						!isEmpty(this.suggestedLinks.starts_len)
							? `/letter-words/${firstParam}-starts-${this.suggestedLinks.starts_len}/`
							: null
					);
				case "contains_len":
					return this.formatWordListSuggestedLink(
						`${firstParam} letter words with ${secondParam?.toUpperCase()}`,
						!isEmpty(this.suggestedLinks.contains_len)
							? `/letter-words/${firstParam}-with-${this.suggestedLinks.contains_len}/`
							: null
					);
				case "ends_len":
					return this.formatWordListSuggestedLink(
						`${firstParam} letter words that end in ${secondParam?.toUpperCase()}`,
						!isEmpty(this.suggestedLinks.ends_len)
							? `/letter-words/${firstParam}-ends-${this.suggestedLinks.ends_len}/`
							: null
					);
			}
			return null;
		},
		formatWordListSuggestedLink(text, href) {
			if (!href) {
				return text;
			}

			let hrefToReturn = href;

			if (Object.keys(this.$route.query).length) {
				hrefToReturn = `${href}?${new URLSearchParams(
					this.$route.query
				)}`;
			}

			return `<a href="${hrefToReturn}">${text}</a>`;
		},
		getBodyParams() {
			const letter = this.$route.params.letter;
			const letterPhrase = letter?.length > 1 ? "Letters" : "Letter";

			switch (this.$route.name) {
				case PAGES.WORDS_STARTING_RESULTS:
					return {
						slug: `starts.${letter}`,
						h1: `Words that start with ${letterPhrase} ${letter.toUpperCase()}`,
						belowResultsAnchor: `more-words-starting-with-${letter}`,
					};

				case PAGES.WORDS_HAVING_RESULTS: {
					const cleanLetter = (letter || "").replace(
						/^(\w+)-and-(\w+)$/,
						"$1$2"
					);

					return {
						slug: `contains.${letter}`,
						h1: `Words with the ${letterPhrase} ${cleanLetter.toUpperCase()}`,
						belowResultsAnchor: `more-words-with-${letter}`,
					};
				}

				case PAGES.WORDS_ENDING_RESULTS:
					return {
						slug: `ends.${letter}`,
						h1: `Words ending in ${letterPhrase} ${letter.toUpperCase()}`,
						belowResultsAnchor: `more-words-ending-in-${letter}`,
					};

				case PAGES.WORDS_BY_LENGTH_RESULTS: {
					const length = this.$route.params.length;
					return {
						slug: `length.${length}`,
						h1: `${length} Letter Words`,
						belowResultsAnchor: `${length}-letter-words-more`,
					};
				}

				case PAGES.WORDS_ENDING_ING:
					return {
						slug: "special.ending-in-ing",
						h1: "Words Ending in ING",
						belowResultsAnchor: "more-words-ending-in-ing",
					};

				case PAGES.WORDS_CONSONANTS:
					return {
						slug: "special.consonants",
						h1: "Words Without Vowels",
						belowResultsAnchor: "more-consonant-words",
					};

				case PAGES.WORDS_WITH_Q_NO_U:
					return {
						slug: "special.q-no-u",
						h1: "Q Words Without U",
						belowResultsAnchor: "more-words-with-q-and-no-u",
					};

				case PAGES.WORDS_VOWELS:
					return {
						slug: "special.vowels",
						h1: "Vowel Words",
						belowResultsAnchor: "more-vowel-words",
					};

				default:
					return null;
			}
		},
	},
};
